import { useMemo} from 'react';
import { useSelector } from 'react-redux';
import Plot from 'react-plotly.js';
import { selectRTFO } from '../../reducers/rtfoSlice';

import { selectUser } from '../../reducers/userSlice';
import { chartColors } from '../../helpers/chartColors';
import { rtfoConversionFactors } from './rtfoConversionFactors';

export const RTFOWellboreTrajectoryChart = ({ currentStageName }) => {


  const user = useSelector(selectUser);
  const rtfo = useSelector(selectRTFO);
  const currentRTFO = rtfo?.value;

  const darkMode = user?.value?.darkMode;
  const units = currentRTFO?.settings?.units || 'metric';

  const wellColors = useMemo(() => {
    const wellColors = {};
    for (let i = 0; i < Object.keys(currentRTFO?.surveys?.wells)?.length; i++) {      
      const wellName = Object.keys(currentRTFO?.surveys.wells)?.[i];
      const color = chartColors[i];
      wellColors[wellName] = color;
    };
    return wellColors;
  }, [currentRTFO?.surveys?.wells]);


  const surveyData = useMemo(() => {
    try {
      const surveyData = { wells: {}};
      for (let i = 0; i < Object.keys(currentRTFO?.surveys?.wells)?.length; i++) {
            
        const wellName = Object.keys(currentRTFO?.surveys.wells)?.[i];
        let wellSurveyData = JSON.parse(JSON.stringify(currentRTFO?.surveys.wells?.[wellName]?.surveyData));
  
        wellSurveyData?.forEach((d,j) => {
          let tD = 0;
          if (j > 0) {
            const dD = (d?.measuredDepth - wellSurveyData?.[j-1]?.measuredDepth) * Math.sin((degreesToRadians(wellSurveyData?.[j-1]?.deviation) + degreesToRadians(d?.deviation)) / 2);
            tD = wellSurveyData[j-1].totalDisplacement + dD;
          };
          d.totalDisplacement = tD;
        });
  
        surveyData.wells[wellName] = wellSurveyData;
      };
  
      return surveyData;
  
      function degreesToRadians(degrees) {
        return (degrees * Math.PI) / 180.0;
      };

    } catch(error) {
      console.log(error);
      return currentRTFO?.surveys;
    };

  }, [currentRTFO?.surveys]);

  const data = useMemo(() => {
    return getChartData(surveyData);

    function getChartData(surveyData) {
      try {
        if (!surveyData) return [];
        
        const traces = [];

        for (let i = 0; i < Object.keys(surveyData?.wells)?.length; i++) {
          
          const wellName = Object.keys(surveyData.wells)?.[i];
          let wellSurveyData = JSON.parse(JSON.stringify(surveyData.wells?.[wellName]));
        
          const XSurvey = [];
          const YSurvey = [];

          wellSurveyData?.forEach((d,j) => {
            XSurvey.push(d.totalDisplacement * (units === 'field' ? rtfoConversionFactors.depth : 1));
            YSurvey.push(d.trueVerticalDepth * (units === 'field' ? rtfoConversionFactors.depth : 1));
          });

          // add traces
          let surveyTrace = {
            x: XSurvey,
            y: YSurvey,
            mode: 'line',
            type: 'scatter',
            xaxis: 'x1',
            yaxis: 'y1',
            name: wellName?.charAt(0)?.toUpperCase()?.concat(wellName?.substring(1, )),
            line: {
              width: 3,
              color: wellColors[wellName]
            },
            hoverinfo: 'text',
            hovertext: YSurvey.map(y => `${wellName?.charAt(0)?.toUpperCase()?.concat(wellName?.substring(1, ))} TVD: ${y.toFixed(2)} ${units === 'field' ? 'ft' : 'm'}`),
          };
          
    
          traces.push(surveyTrace);

        };

        return traces;
      } catch (error) {
        console.log(error);
        return [];
      };

      
    };
  }, [surveyData, units, wellColors]);
  
  // Calculate average TVD between all wells
  let averageTVD = Object.keys(surveyData?.wells)?.map(wellName => surveyData?.wells?.[wellName]?.slice(-1)?.[0]?.trueVerticalDepth)?.reduce((tot, curr) => tot + curr, 0) / Object.keys(surveyData?.wells)?.length;
  averageTVD = averageTVD * (units === 'field' ? rtfoConversionFactors.depth : 1);

  // Determine coordinates of the current stage
  const currentStageNumber = String(Number(String(currentStageName?.split('_')?.[2])?.replace(/[^0-9.]+|^0(?=[1-9])/g, '')?.trim()));
  const currentWellName = currentStageName?.split('_')?.[1]?.toLowerCase();
  
  const currentStageCoordinates = useMemo(() => {
    const currentStageClusters = currentRTFO?.perfSchedule?.wells?.[currentWellName]?.stages?.[currentStageNumber]?.clusters;

    if (!currentStageClusters) return { tvd: 0, top: 0, bottom: 0 };

    const currentStageMDRange = [currentStageClusters[Object.keys(currentStageClusters)?.slice(-1)?.[0]]?.shotDepth, currentStageClusters?.['1']?.shotDepth];
    const currentStageTVD = surveyData?.wells?.[currentWellName]?.find(d => d.measuredDepth >= currentStageMDRange?.[0])?.trueVerticalDepth;
    const currentStageDisplacementTop = surveyData?.wells?.[currentWellName]?.findLast(d => d.measuredDepth <= currentStageMDRange?.[0])?.totalDisplacement;
    const currentStageDisplacementBottom = currentStageDisplacementTop + 50;

    return { 
      tvd: currentStageTVD * (units === 'field' ? rtfoConversionFactors.depth : 1), 
      top: currentStageDisplacementTop * (units === 'field' ? rtfoConversionFactors.depth : 1), 
      bottom: currentStageDisplacementBottom * (units === 'field' ? rtfoConversionFactors.depth : 1)
    };
  }, [currentRTFO?.perfSchedule?.wells, currentStageNumber, currentWellName, surveyData?.wells, units]);

  const layout = {
    yaxis: { // Survey y axis
      linecolor: darkMode ? 'lightgrey' : 'black',
      gridcolor: darkMode ? 'grey' : 'lightgrey',
      color: darkMode ? 'lightgrey' : 'black',
      title: {
        text: `TVD (${(units === 'field' ? 'ft' : 'm')})`,
        font: {
          size: 12
        }
      },
      range: units === 'field' ? [averageTVD + 300, averageTVD - 300] : [averageTVD + 100, averageTVD - 100],
      rangemode: 'nonnegative'
    },
    xaxis: { // Survey x axis
      linecolor: darkMode ? 'lightgrey' : 'black',
      gridcolor: darkMode ? 'grey' : 'lightgrey',
      color: darkMode ? 'lightgrey' : 'black',
      title: {
        text: `Horizontal Displacement (${units === 'field' ? 'ft' : 'm'})`,
        standoff: 10,
        font: {
          size: 12
        },
      },
      autorange: true,
      rangemode: 'nonnegative',
      showspikes: true,
      spikethickness: 2,
      spikedash: "dot",
      spikecolor: "#999999",
      spikemode: "across",
    },
    hovermode: "x",
    hoverdistance: 100, // Distance to show hover label of data point
    spikedistance: 1000,
    autosize: true,
    showlegend: true,
    legend: {
      font: {
        color: darkMode ? 'lightgrey' : 'grey'
      },
      orientation: "h",
      yanchor: "top",
      yref: 'paper',
      y: -0.125,
    },
    uirevision:'true',
    annotations: [
      {
        text: "Well Trajectories",
          font: {
          size: 14,
           color: darkMode ? 'lightgrey' : 'black'
        },
        showarrow: false,
        align: 'center',
        x: 0.5, //position in x domain
        y: 1.075, //position in y domain
        xref: 'paper',
        yref: 'paper',
      },
      {
        text: `${currentWellName?.charAt(0)?.toUpperCase()?.concat(currentWellName?.substring(1, ))} Stg. ${currentStageName?.split('_')?.[2]}`,
          font: {
            size: 10,
            color: wellColors?.[currentWellName]
          },
        showarrow: false,
        align: 'center',
        x: (currentStageCoordinates?.top + currentStageCoordinates?.bottom) / 2, //position in x domain
        y: currentStageCoordinates?.tvd - currentRTFO?.settings?.fracHeight / 2 - 10, //position in y domain
        xref: 'x',
        yref: 'y',
      },
    ],
    shapes: [
      {
        type: 'rect',
        xref: 'x',
        yref: 'y',
        x0: currentStageCoordinates?.bottom,
        y0: currentStageCoordinates?.tvd - currentRTFO?.settings?.fracHeight / 2,
        x1: currentStageCoordinates?.top,
        y1: currentStageCoordinates?.tvd + currentRTFO?.settings?.fracHeight / 2,
        fillcolor: wellColors[currentWellName],
        opacity: 1,
        line: {
            width: 0
        }
      },
    ],
    margin: {"t": 50, "l": 60, "r": 60, "b": 50},
    images: [
      {
        x: 0.025,
        y: 1.015,
        sizex: 0.1,
        sizey: 0.05,
        source: process.env.PUBLIC_URL + `/img/ssd_logomark_${darkMode ? 'white' : 'primary'}.png`,
        xref: "paper",
        yref: "paper",
        xanchor: "right",
        yanchor: "bottom",
        opacity: 1
      }
    ],
    plot_bgcolor: "transparent",
    paper_bgcolor: (darkMode ? 'transparent' : '#fff')
  };

  const config = {
    displaylogo: false,
    toImageButtonOptions: {
      format: 'png', // one of png, svg, jpeg, webp
      filename: currentRTFO.name + "_trajectories_" + (new Date().toISOString().split('T')[0]),
      height: 1440,
      width: 960,
      scale: 1 // Multiply title/legend/axis/canvas sizes by this factor
    }
  };

  return (
    <Plot
      data={data}
      layout={layout}
      config={config}
      useResizeHandler={true}
      style={{width: '100%', height: '100%'}}
    />
  );
};
